import { colors, COLORS } from '@constants/colors';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const PANEL_BG =
  'linear-gradient(135deg, rgba(28, 28, 34, 0.88) 0%, rgba(16, 16, 20, 0.88) 100%)';
const PANEL_BORDER = '1px solid rgba(255, 255, 255, 0.08)';
const PANEL_RADIUS = '20px';
const PANEL_BLUR = 'blur(18px)';

export const useAiInspectorStyles = () => ({
  panel: {
    background: PANEL_BG,
    border: PANEL_BORDER,
    borderRadius: PANEL_RADIUS,
    backdropFilter: PANEL_BLUR,
    color: COLORS.WHITE,
    boxShadow: '0 18px 50px rgba(0, 0, 0, 0.45)',
    display: 'flex',
    flexDirection: 'column',
    maxHeight: 'calc(100vh - 208px)',
    overflow: 'hidden',
  },

  header: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '11px',
    padding: '16px 16px 0',
  },
  headerIcon: {
    width: 34,
    height: 34,
    borderRadius: '10px',
    backgroundColor: 'rgba(255, 255, 255, 0.06)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#cfd2dd',
    flex: 'none',
    '& svg': {
      fontSize: 18,
    },
  },
  headerTitleColumn: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  headerTitle: {
    fontSize: '15.5px',
    fontWeight: 750,
    lineHeight: 1.25,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  headerSubRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    flexWrap: 'wrap',
  },
  monoIdChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontFamily: MONO,
    fontSize: '11px',
    color: '#9da1ae',
  },
  copyButton: {
    color: '#8c90a0',
    padding: '2px',
    '&:hover': {
      color: COLORS.WHITE,
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  kindChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '10px',
    fontWeight: 750,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#b9bcd0',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    borderRadius: '999px',
    padding: '2.5px 8px',
  },
  aliasChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    fontSize: '10px',
    fontWeight: 750,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#a3a8b5',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    borderRadius: '999px',
    padding: '2.5px 8px',
    cursor: 'help',
  },
  closeButton: {
    color: '#b9bdc8',
    padding: '4px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.07)',
      color: COLORS.WHITE,
    },
  },

  body: {
    flex: 1,
    overflowY: 'auto',
    padding: '12px 16px 16px',
    minHeight: 0,
  },

  liveStrip: {
    display: 'flex',
    gap: '9px',
    alignItems: 'flex-start',
    border: '1px solid rgba(240, 177, 76, 0.4)',
    backgroundColor: 'rgba(240, 177, 76, 0.12)',
    color: '#f5cf8d',
    borderRadius: '11px',
    padding: '9px 11px',
    fontSize: '11.5px',
    lineHeight: 1.45,
    marginBottom: '12px',
  },
  liveStripIcon: {
    flex: 'none',
    marginTop: '1px',
    color: '#f0b14c',
    fontSize: 14,
  },
  liveStripStrong: {
    color: '#f5cf8d',
    fontWeight: 750,
  },

  tabs: {
    display: 'flex',
    gap: '4px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    border: '1px solid rgba(255, 255, 255, 0.055)',
    borderRadius: '12px',
    padding: '4px',
    marginBottom: '12px',
  },
  tab: {
    flex: 1,
    border: 'none',
    backgroundColor: 'transparent',
    color: '#b3b7c3',
    fontSize: '12px',
    fontWeight: 700,
    borderRadius: '9px',
    padding: '7px 0',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.12s ease',
    '&:hover': {
      color: COLORS.WHITE,
    },
  },
  tabActive: {
    backgroundColor: 'rgb(110, 108, 246)',
    color: COLORS.WHITE,
    '&:hover': {
      color: COLORS.WHITE,
    },
  },
  tabDisabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    '&:hover': {
      color: '#b3b7c3',
    },
  },

  card: {
    backgroundColor: 'rgba(255, 255, 255, 0.045)',
    border: '1px solid rgba(255, 255, 255, 0.055)',
    borderRadius: '14px',
    padding: '12px',
    marginBottom: '12px',
  },
  cardHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '9px',
  },
  cardHeaderTitle: {
    fontSize: '10.5px',
    fontWeight: 800,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: '#9296a3',
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
  },
  iconButton: {
    width: 28,
    height: 28,
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'transparent',
    border: 'none',
    color: '#b8bcc8',
    cursor: 'pointer',
    fontFamily: 'inherit',
    transition: 'all 0.12s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      color: COLORS.WHITE,
    },
  },
  iconButtonDanger: {
    '&:hover': {
      color: '#f06d6d',
      backgroundColor: 'rgba(240, 109, 109, 0.13)',
    },
  },

  draftChip: {
    display: 'inline-flex',
    alignItems: 'center',
    fontSize: '10px',
    fontWeight: 750,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: '#f0b14c',
    border: '1px solid rgba(240, 177, 76, 0.4)',
    borderRadius: '999px',
    padding: '2.5px 8px',
    marginLeft: '8px',
  },

  // Timeline
  timeline: {
    position: 'relative',
    paddingLeft: '22px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '8px',
      top: '8px',
      bottom: '8px',
      width: '2px',
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '2px',
    },
  },
  step: {
    position: 'relative',
    padding: '7px 4px 7px 6px',
    borderRadius: '10px',
    marginBottom: '4px',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: '-18px',
      top: '13px',
      width: '10px',
      height: '10px',
      borderRadius: '50%',
      backgroundColor: '#3a3b45',
      border: '2px solid #15161c',
      boxShadow: '0 0 0 1.5px rgba(255, 255, 255, 0.18)',
    },
  },
  stepText: {
    fontSize: '12px',
    color: '#d5d7e0',
    lineHeight: 1.45,
  },
  stepMeta: {
    display: 'flex',
    gap: '8px',
    marginTop: '4px',
    fontSize: '10px',
    color: '#828794',
    fontFamily: MONO,
    alignItems: 'center',
  },
  stepQuiz: {
    color: '#f0b14c',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
    fontFamily: 'inherit',
    fontWeight: 650,
  },

  // Translations bar
  translationsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    marginTop: '11px',
    flexWrap: 'wrap',
  },
  translationsLabel: {
    fontSize: '10.5px',
    fontWeight: 800,
    letterSpacing: '0.07em',
    textTransform: 'uppercase',
    color: '#9296a3',
    marginRight: '2px',
    display: 'inline-flex',
    alignItems: 'center',
    '& svg': {
      fontSize: 12,
    },
  },
  langChip: {
    fontSize: '10px',
    fontWeight: 750,
    fontFamily: MONO,
    border: '1px solid rgba(255, 255, 255, 0.09)',
    borderRadius: '7px',
    padding: '3px 7px',
    color: '#8d92a0',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '4px',
  },
  langChipDone: {
    color: '#a9eec3',
    borderColor: 'rgba(63, 207, 110, 0.35)',
    backgroundColor: 'rgba(63, 207, 110, 0.14)',
  },
  linkButton: {
    backgroundColor: 'transparent',
    border: 'none',
    color: '#bdbef6',
    fontSize: '11px',
    fontWeight: 700,
    cursor: 'pointer',
    padding: '2px 4px',
    borderRadius: '6px',
    fontFamily: 'inherit',
    '&:hover': {
      backgroundColor: 'rgba(110, 108, 246, 0.14)',
    },
    '&:disabled': {
      opacity: 0.6,
      cursor: 'not-allowed',
    },
  },

  // Meta chips
  metaChips: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
    marginTop: '10px',
  },
  metaChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: '#c6c9d3',
    borderRadius: '999px',
    padding: '3.5px 9px',
    fontSize: '10.5px',
    fontWeight: 650,
    '& svg': {
      color: '#8e92a2',
      fontSize: 12,
    },
  },

  // Empty / placeholder
  emptyHint: {
    fontSize: '12.5px',
    color: '#a7abb8',
    lineHeight: 1.55,
    marginBottom: '10px',
  },

  // Centered empty state (inspector tabs) — design spec § 05.
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '9px',
    padding: '18px 14px 8px',
  },
  emptyIcon: {
    width: 46,
    height: 46,
    borderRadius: '50%',
    border: '1.5px dashed rgba(255, 255, 255, 0.18)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#8b8f9c',
    '& svg': {
      fontSize: 21,
    },
  },
  emptyTitle: {
    fontSize: '13px',
    fontWeight: 680,
    color: COLORS.WHITE,
  },
  emptyDesc: {
    fontSize: '11.5px',
    lineHeight: 1.5,
    color: '#a7abb8',
    maxWidth: '220px',
  },
  ghostButton: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    height: '30px',
    padding: '0 13px',
    borderRadius: '9px',
    border: '1px solid rgba(255, 255, 255, 0.18)',
    backgroundColor: 'transparent',
    color: '#e8e9f0',
    fontSize: '12px',
    fontWeight: 680,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginTop: '2px',
    '& svg': {
      fontSize: 13,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.07)',
      borderColor: 'rgba(255, 255, 255, 0.28)',
    },
  },
  ctaButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '9px',
    width: '100%',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    backgroundColor: 'rgba(255, 255, 255, 0.04)',
    color: '#e8e9f0',
    borderRadius: '12px',
    padding: '11px 13px',
    fontSize: '12.5px',
    fontWeight: 680,
    cursor: 'pointer',
    fontFamily: 'inherit',
    textAlign: 'left',
    '& svg': {
      color: '#b9aef8',
      fontSize: 16,
    },
    '&:hover': {
      borderColor: 'rgba(110, 108, 246, 0.5)',
      backgroundColor: 'rgba(110, 108, 246, 0.1)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  ctaSecondary: {
    color: COLORS.WHITE,
    '& small': {
      display: 'block',
      fontWeight: 500,
      color: '#8d91a0',
      fontSize: '10.5px',
      marginTop: '1px',
    },
  },

  // Tester
  testerInputRow: {
    display: 'flex',
    gap: '7px',
    marginTop: '2px',
  },
  testerInput: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.32)',
    border: '1px solid rgba(255, 255, 255, 0.055)',
    borderRadius: '10px',
    padding: '9px 11px',
    color: COLORS.WHITE,
    fontSize: '12.5px',
    fontFamily: 'inherit',
    outline: 'none',
    minWidth: 0,
    '&:focus': {
      borderColor: 'rgba(110, 108, 246, 0.5)',
    },
    '&::placeholder': {
      color: '#71757f',
    },
  },
  sendButton: {
    width: 38,
    height: 36,
    borderRadius: '10px',
    border: 'none',
    backgroundColor: 'rgb(110, 108, 246)',
    color: COLORS.WHITE,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    '&:hover': {
      filter: 'brightness(1.1)',
    },
    '&:disabled': {
      opacity: 0.5,
      cursor: 'default',
    },
  },
  userBubble: {
    textAlign: 'right',
    margin: '10px 0 6px',
    '& span': {
      backgroundColor: 'rgba(255, 255, 255, 0.07)',
      borderRadius: '11px 11px 3px 11px',
      padding: '6px 10px',
      display: 'inline-block',
      maxWidth: '88%',
      textAlign: 'left',
      fontSize: '11.5px',
      color: '#dadce4',
    },
  },
  assistantBubble: {
    fontSize: '12px',
    color: '#dadce4',
    lineHeight: 1.55,
    backgroundColor: 'rgba(110, 108, 246, 0.1)',
    border: '1px solid rgba(110, 108, 246, 0.25)',
    borderRadius: '3px 12px 12px 12px',
    padding: '9px 11px',
  },
  bubbleSources: {
    marginTop: '7px',
    display: 'flex',
    gap: '5px',
    flexWrap: 'wrap',
  },
  bubbleMeta: {
    fontSize: '10px',
    color: '#7e8390',
    fontFamily: MONO,
    marginTop: '6px',
  },
  thinkingDots: {
    display: 'inline-flex',
    gap: '4px',
    padding: '9px 12px',
    '& i': {
      width: 5,
      height: 5,
      borderRadius: '50%',
      backgroundColor: '#a9abf0',
      animation: 'aiThinkingPulse 1s infinite',
    },
    '& i:nth-of-type(2)': { animationDelay: '0.18s' },
    '& i:nth-of-type(3)': { animationDelay: '0.36s' },
    '@keyframes aiThinkingPulse': {
      '0%, 100%': { opacity: 1 },
      '50%': { opacity: 0.4 },
    },
  },

  // Knowledge rows
  knowledgeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    padding: '8px 6px',
    borderRadius: '10px',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.04)',
    },
  },
  knowledgeRowDivider: {
    borderTop: '1px solid rgba(255, 255, 255, 0.055)',
  },
  knowledgeIcon: {
    color: '#9fa3b0',
    flex: 'none',
    fontSize: 16,
  },
  knowledgeText: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: '1px',
  },
  knowledgeTitle: {
    fontSize: '12.5px',
    fontWeight: 620,
    color: COLORS.WHITE,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  knowledgeMeta: {
    fontSize: '10.5px',
    color: '#8a8e9b',
    fontFamily: MONO,
  },
  knowledgeBadge: {
    fontSize: '10px',
    fontWeight: 750,
    letterSpacing: '0.04em',
    borderRadius: '999px',
    padding: '3px 8px',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    flex: 'none',
    '& svg': {
      fontSize: 11,
    },
  },
  knowledgeBadgeReady: {
    color: '#9ceab8',
    backgroundColor: 'rgba(63, 207, 110, 0.14)',
  },
  knowledgeBadgePending: {
    color: '#f3d39b',
    backgroundColor: 'rgba(240, 177, 76, 0.12)',
  },
  knowledgeBadgeFailed: {
    color: '#f6a8a8',
    backgroundColor: 'rgba(240, 109, 109, 0.13)',
  },
  knowledgeError: {
    fontSize: '10.5px',
    color: '#f0a3a3',
    fontFamily: MONO,
    padding: '4px 6px 0',
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  knowledgeAddButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '7px',
    justifyContent: 'center',
    width: '100%',
    marginTop: '9px',
    border: '1px dashed rgba(255, 255, 255, 0.2)',
    backgroundColor: 'transparent',
    color: '#c8cad4',
    borderRadius: '10px',
    padding: '7.5px',
    fontSize: '11.5px',
    fontWeight: 650,
    cursor: 'pointer',
    fontFamily: 'inherit',
    '&:hover': {
      borderColor: 'rgba(110, 108, 246, 0.5)',
      color: COLORS.WHITE,
    },
  },

  // Regions chips for ctx-only entity
  regionChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    border: '1px solid rgba(255, 255, 255, 0.09)',
    backgroundColor: 'rgba(255, 255, 255, 0.03)',
    color: '#c6c9d3',
    borderRadius: '999px',
    padding: '3.5px 9px',
    fontSize: '10.5px',
    fontWeight: 650,
    '& svg': {
      color: '#8e92a2',
      fontSize: 12,
    },
  },

  footHint: {
    fontSize: '10.5px',
    color: '#777c89',
    lineHeight: 1.5,
    marginTop: '4px',
  },

  spinning: {
    animation: 'aiSpin 1.1s linear infinite',
    '@keyframes aiSpin': {
      to: { transform: 'rotate(360deg)' },
    },
  },
});

export const KIND_LABEL_KEY: Record<
  'area' | 'ui' | 'object' | 'landing' | 'ctx',
  { key: string; fallback: string }
> = {
  area: { key: 'editorMode.aiKind.area', fallback: 'Area entry' },
  ui: { key: 'editorMode.aiKind.ui', fallback: 'UI button' },
  object: { key: 'editorMode.aiKind.object', fallback: '3D object' },
  landing: { key: 'editorMode.aiKind.landing', fallback: 'On join' },
  ctx: { key: 'editorMode.aiKind.ctx', fallback: 'Knowledge' },
};
